// apps/web/src/components/RequireAuth.tsx
//
// Server-verified route guards (phase-1-5 §1). The <RequireAuth>
// wrapper renders a loading state while /me is in flight, redirects
// to /signin?next=... on a confirmed 401, and only renders children
// after a confirmed 200. It NEVER renders protected content
// optimistically while the auth check is pending.
//
// <RequireAdmin> wraps <RequireAuth> and additionally requires
// me.role === 'admin'. The backend check is the load-bearing
// security boundary; this wrapper is a UX convenience to avoid
// flashing a protected view before the redirect fires.

import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth, useMeOrNull } from '../lib/auth.js';
import type { Role } from '../lib/api.js';
import { SkeletonBlock } from './ui/Skeleton.js';

export interface RequireAuthProps {
  children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps): JSX.Element {
  const { me } = useAuth();
  const location = useLocation();

  if (me.status === 'pending') {
    return (
      <div role="status" aria-live="polite" aria-busy="true" className="mx-auto max-w-2xl px-4 py-12">
        <SkeletonBlock className="h-8 w-48" />
        <SkeletonBlock className="mt-4 h-4 w-full" />
        <SkeletonBlock className="mt-2 h-4 w-5/6" />
      </div>
    );
  }
  if (me.status === 'unauthenticated') {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/signin?next=${next}`} replace />;
  }
  return <>{children}</>;
}

export interface RequireAdminProps {
  children: ReactNode;
}

export function RequireAdmin({ children }: RequireAdminProps): JSX.Element {
  const user = useMeOrNull();
  if (user && user.role !== ('admin' satisfies Role)) {
    // Authenticated but not admin. Redirect to dashboard (the only
    // safe authenticated landing). The backend will 403 every
    // admin API call regardless of where the user is.
    return <Navigate to="/dashboard" replace />;
  }
  return <RequireAuth>{children}</RequireAuth>;
}
